/**
 * Yönetim ekranlarının metinleri: tedarikçiler ve kullanıcılar (`Admin.tsx`, CRM-A13).
 *
 * İki bölüm, iki ayrı izin. Sekmeyi gizlemek yetkilendirme değil: arkadaki her uç sunucuda
 * yeniden kontrol ediliyor ve izinsiz istek 403 dönüyor. Buradaki metinler yalnız ekranın
 * dilini taşıyor, kararı değil.
 *
 * Kelime seçimleri `docs/tr-sozluk.md` içinde.
 */

/**
 * Tedarikçi türü. Değerler `SupplierKind` (`prisma/schema.prisma`).
 *
 * `ortak.ts` içindeki `KALEM_TURU` sipariş kaleminin türü; bu ise tedarikçinin ne iş yaptığı.
 * İkisi benzer kelimeler kullanıyor ama aynı liste değil: `PAYMENT`, `EMAIL`, `SMS` bir sipariş
 * kalemi olamaz, tedarikçi olabilir.
 */
export const TEDARIKCI_TURU: Readonly<Record<string, string>> = {
  TOUR: 'Tur',
  HOTEL: 'Otel',
  ACTIVITY: 'Aktivite',
  VISA: 'Vize',
  PAYMENT: 'Ödeme',
  EMAIL: 'E-posta',
  SMS: 'SMS',
};

/**
 * Tedarikçi durumu. Değerler `SupplierStatus` (`prisma/schema.prisma`).
 *
 * `RETIRED` "silinmiş" değil: geçmiş siparişler hâlâ o tedarikçiye ait, o yüzden kayıt duruyor
 * ve ekranda "Kapandı" yazıyor.
 */
export const TEDARIKCI_DURUMU: Readonly<Record<string, string>> = {
  ACTIVE: 'Etkin',
  ONBOARDING: 'Bağlanıyor',
  SUSPENDED: 'Askıda',
  RETIRED: 'Kapandı',
};

export const YONETIM_METIN = {
  tedarikciler: {
    ustBaslik: 'FİNANS · TEDARİKÇİLER',
    baslik: 'Tedarikçiler',
    tarif: 'Kime ulaşacağınız ve kimin elle çalıştığı. Bakiye ve sözleşme tutarı burada yok; onlar ayrı bir iş.',
    hata: 'Tedarikçiler yüklenemedi.',
    yalnizElle: 'Yalnız elle çalışanlar',
    sutunTedarikci: 'Tedarikçi',
    sutunTur: 'Tür',
    sutunDurum: 'Durum',
    sutunCalisma: 'Çalışma',
    sutunMuhatap: 'Muhatap',
    sutunAcikSoru: 'Açık soru',
    /** Bir insanın işlediği tedarikçi ile uca bağlı olan arasındaki fark operasyonun gününü değiştiriyor. */
    calismaElle: 'Elle',
    calismaApi: 'API',
    ipListesi: 'IP beyaz listesi',
    /** Boş bir hücre "bilgi yok" demiyor; "kimse girmemiş" diyor ve ikisi farklı. */
    muhatapYok: 'Girilmemiş',
    bosBaslik: 'Kayıt yok',
    bosTarif: 'Süzgeci kaldırın ya da bir tedarikçi kaydı açın.',
    sorularUstBaslik: 'AÇIK SORULAR',
    sorularBaslik: 'Tedarikçiye sorulacaklar',
    soruSahibi: 'sahibi',
    soruSahibiPasif: '(devre dışı, devredilmeli)',
    soruYok: 'Bekleyen soru yok.',
  },

  kullanicilar: {
    ustBaslik: 'YÖNETİM · ÇALIŞANLAR',
    baslik: 'Kullanıcılar',
    tarif: 'Silme yok, devre dışı bırakma var: silinen bir kullanıcının denetim kaydındaki kararları sahipsiz kalır.',
    hesapEki: 'hesap',
    hesaptanSayi: (gosterilen: string, toplam: string) => `${toplam} hesaptan ${gosterilen}`,
    hata: 'Kullanıcılar yüklenemedi.',
    islemHatasi: 'İşlem tamamlanamadı.',
    sutunAd: 'Ad',
    sutunEposta: 'E-posta',
    sutunRol: 'Rol',
    sutunDurum: 'Durum',
    sutunSonGiris: 'Son giriş',
    sutunIslem: 'İşlem',
    /** Seçim kutusunun erişilebilir adı: hangi kullanıcının rolü olduğunu söylüyor. */
    rolSeciciEtiketi: (ad: string) => `${ad} için rol`,
    /** Reddin sebebi ekranda kalıyor; sebep sorulmadan rol değişmiyor. */
    rolSebebi: 'Rol değişikliğinin sebebi?',
    kapatmaSebebi: 'Kapatma sebebi?',
    geriAcmaSebebi: 'Geri açma sebebi?',
    durumEtkin: 'Etkin',
    durumPasif: 'Devre dışı',
    kapat: 'Devre dışı bırak',
    geriAc: 'Geri aç',
    kapatildi: 'devre dışı bırakıldı',
    geriAcildi: 'geri açıldı',
    hicGiris: 'Hiç',
    /**
     * "Yeni kullanıcı" formu (10.09.2026). `api.ts`teki `createUser` ve sunucudaki
     * `POST /users` zaten vardı; eksik olan yalnız bu formdu — hesaplar bugüne kadar betikle
     * açılıyordu.
     */
    yeniBaslik: 'Yeni kullanıcı',
    yeniAcTarif: 'Parola yazılmaz. Hesap açılınca tek kullanımlık bir davet bağlantısı çıkar; kullanıcı parolasını o bağlantıyla kendisi koyar.',
    yeniAdEtiketi: 'Görünen ad',
    yeniEpostaEtiketi: 'E-posta',
    yeniRolEtiketi: 'Rol',
    yeniAc: 'Kullanıcı aç',
    yeniAciliyor: 'Açılıyor…',
    yeniAcildi: (ad: string) => `${ad} açıldı. Davet bağlantısı aşağıda.`,
    yeniAcHatasi: 'Kullanıcı açılamadı.',
    /** Süzgeç çubuğu. */
    araEtiketi: 'Ad ya da e-posta ara',
    araYer: 'Ad ya da e-posta',
    rolSuzgeci: 'Rol',
    tumRoller: 'Tüm roller',
    durumSuzgeci: 'Durum',
    durumHepsi: 'Hepsi',
    durumYalnizEtkin: 'Etkin',
    durumYalnizPasif: 'Devre dışı',
    sonucYok: 'Bu süzgece uyan kullanıcı yok.',
    /** Satır eylemleri ve durum yazıları. */
    durumBekliyor: 'Parola bekleniyor',
    durumBekliyorDavet: 'Davet bekliyor',
    sutunOturum: 'Açık oturum',
    sutunKullanici: 'Kullanıcı',
    benim: 'sen',
    parolaSifirla: 'Parola sıfırla',
    davetYenile: 'Davet bağlantısını yenile',
    oturumlariKapat: 'Oturumları kapat',
    islemYok: 'Kendi hesabın: parolanı Profil sayfasından değiştir.',
    /** Eylem kutuları. */
    sebepEtiketi: 'Sebep',
    sebepKural: 'Denetim kaydına yazılır, en az 3 karakter.',
    onayla: 'Onayla',
    vazgec: 'Vazgeç',
    tamam: 'Tamam',
    rolKutusuBaslik: (ad: string, eski: string, yeni: string) => `${ad}: ${eski} yerine ${yeni}`,
    rolKutusuUyari: 'Kullanıcının açık oturumları kapanır, yeniden girmesi gerekir.',
    durumKutusuKapat: (ad: string) => `${ad} devre dışı bırakılsın mı?`,
    durumKutusuAc: (ad: string) => `${ad} geri açılsın mı?`,
    durumKutusuUyari: 'Devre dışı bırakılan kullanıcının açık oturumları hemen kapanır.',
    sifirlaKutusuBaslik: (ad: string) => `${ad} için parola sıfırlansın mı?`,
    sifirlaKutusuUyari:
      'Parolayı sen görmezsin ve seçmezsin: tek kullanımlık bir bağlantı çıkar. Kullanıcının açık oturumları şimdi kapanır, eski parolası bağlantı kullanılana kadar geçerli kalır.',
    oturumKutusuBaslik: (ad: string) => `${ad} için açık oturumlar kapatılsın mı?`,
    oturumlarKapandi: (ad: string, adet: number) => `${ad}: ${adet} oturum kapatıldı.`,
    /** Bağlantı kutusu. */
    baglantiBaslikDavet: (ad: string) => `${ad} için davet bağlantısı`,
    baglantiBaslikSifirla: (ad: string) => `${ad} için parola sıfırlama bağlantısı`,
    baglantiTarif:
      'Bağlantıyı kullanıcıya kendin ilet. Bir kez kullanılır, süresi dolunca ya da yenisi üretilince ölür. Bu kutu kapanınca bağlantı bir daha gösterilmez.',
    baglantiSure: (bitis: string) => `Geçerlilik sonu: ${bitis}`,
    baglantiEtiketi: 'Bağlantı',
    baglantiKopyala: 'Bağlantıyı kopyala',
    baglantiKopyalandi: 'Kopyalandı',
    baglantiKopyalanamadi: 'Kopyalanamadı: alanı seçip elle kopyala.',
    sifirlandiNot: (ad: string, adet: number) => `${ad}: bağlantı üretildi, ${adet} açık oturum kapatıldı.`,
  },
  parolaKur: {
    ustBaslik: 'HESAP',
    baslik: 'Parolanı belirle',
    kontrol: 'Bağlantı kontrol ediliyor…',
    gecersiz: 'Bu bağlantı geçersiz ya da süresi dolmuş. Yöneticiden yenisini iste.',
    selam: (ad: string) => `${ad}, hesabın için bir parola belirle.`,
    parola: 'Yeni parola',
    tekrar: 'Parolayı tekrar yaz',
    kural: 'En az 12 karakter. Kolay tahmin edilen parolalar, adın ve e-postan kabul edilmez. Uzun bir cümle iyi bir paroladır.',
    uyusmuyor: 'İki parola aynı değil.',
    kaydet: 'Parolayı kaydet',
    kaydediliyor: 'Kaydediliyor…',
    hata: 'Parola kaydedilemedi.',
    tamam: 'Parolan kaydedildi. Şimdi giriş yapabilirsin.',
    girisYap: 'Giriş sayfasına git',
  },
} as const;
